import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchAssets } from '@/lib/api';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Server, Database, Globe, Cpu, Cloud, Network, Search, ExternalLink } from 'lucide-react';

const CI_ICONS = {
  SERVER: Server, DATABASE: Database, NETWORK_DEVICE: Network,
  APPLICATION: Globe, CLOUD_SERVICE: Cloud, INFRASTRUCTURE: Cpu,
};

const ENV_COLORS = {
  PROD: 'text-[var(--nexus-danger)] bg-red-500/10 border-red-500/20',
  STAGING: 'text-[var(--nexus-warning)] bg-amber-500/10 border-amber-500/20',
  DEV: 'text-[var(--nexus-info)] bg-indigo-500/10 border-indigo-500/20',
};

export default function AssetsPage() {
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: assets, isLoading } = useQuery({
    queryKey: ['assets', filterType],
    queryFn: () => fetchAssets({ ci_type: filterType || undefined }),
    refetchInterval: 30000,
  });

  const filtered = (assets || []).filter(a =>
    !searchTerm || a.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="assets-page" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Asset Management</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">CMDB managed by ATLAS agent • {filtered.length} CIs</p>
        </div>
        <Link to="/topology" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0d3d4d] border border-cyan-500/30 text-[var(--nexus-accent)] text-xs font-semibold hover:bg-[#0f4a5c] transition-colors" data-testid="view-topology">
          <Network size={12} /> View Topology
        </Link>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)]">
          <Search size={12} className="text-[var(--nexus-text-muted)]" />
          <input data-testid="assets-search" type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search assets..." className="bg-transparent text-xs text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none w-48" />
        </div>
        <select data-testid="assets-filter-type" value={filterType} onChange={e => setFilterType(e.target.value)} className="px-2.5 py-1.5 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-xs text-[var(--nexus-text-secondary)] outline-none">
          <option value="">All Types</option>
          <option value="SERVER">Server</option>
          <option value="APPLICATION">Application</option>
          <option value="DATABASE">Database</option>
          <option value="NETWORK_DEVICE">Network Device</option>
          <option value="CLOUD_SERVICE">Cloud Service</option>
          <option value="INFRASTRUCTURE">Infrastructure</option>
        </select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-32 rounded-xl bg-[var(--nexus-bg-elevated)] animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="assets-grid">
          {filtered.map(asset => {
            const Icon = CI_ICONS[asset.type] || Server;
            const envClass = ENV_COLORS[asset.environment] || ENV_COLORS.DEV;
            return (
              <div key={asset.id} className="nexus-card relative overflow-hidden p-4 hover:bg-white/[0.01] transition-colors group">
                <Link to={`/assets/${asset.id}`} className="absolute inset-0 z-10" />
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--nexus-accent)] bg-opacity-10">
                    <Icon size={18} className="text-[var(--nexus-accent)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-[var(--nexus-text-primary)]">{asset.name}</span>
                      <StatusDot status={asset.status === 'ACTIVE' ? 'active' : asset.status === 'MAINTENANCE' ? 'warning' : 'idle'} pulse={asset.status === 'ACTIVE'} size="sm" />
                      <ExternalLink size={10} className="text-[var(--nexus-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-[var(--nexus-text-muted)]">{asset.type}</span>
                      <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded border ${envClass}`}>{asset.environment}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px] text-[var(--nexus-text-muted)]">Owner: <span className="text-[var(--nexus-text-secondary)]">{asset.owner || '-'}</span></span>
                      {asset.metadata?.region && <span className="font-mono text-[10px] text-[var(--nexus-text-muted)]">{asset.metadata.region}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full nexus-card p-12 text-center">
              <Server size={32} className="text-[var(--nexus-text-muted)] mx-auto mb-3" />
              <p className="text-sm text-[var(--nexus-text-muted)]">{searchTerm ? 'No assets match your search' : 'No assets discovered'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
